import { nextTick } from 'vue'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { useAuthHelpers } from '@/@vxapp/Auth/useAuthHelpers'
import { isGoogleCallbackRoute } from '@/access/googleCallback'
import { canPageMeta } from '@/@layouts/plugins/casl'
import { useNotificationsStore } from '@/@vxapp/useNotificationsStore'
import { measureRouterGuard, endMeasurement } from '@/@vxapp/Auth/performance'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { Errors } from '@/@vxapp/@Enums/Error'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { StorageManager, StorageKey } from '@/@vxapp/@core/AppStorage/StorageManager'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { authReturnRouteOwner, captureAuthReturnRoute } from '@/plugins/2.router/authReturnRoute'
import { admittedLandingRoute } from '@/plugins/2.router/admittedLanding'

const log = createLogger('RouterGuard')
const authRouteNames = new Set(Object.values(PageMeta.Auth).map(authRoute => authRoute.name))

export function setupGuards(router) {
  router.beforeEach(async (to, from) => {
    if (isGoogleCallbackRoute(to)) return true

    const guardMeasurement = measureRouterGuard(to.name || to.path)

    const serviceGuestRouteNames = new Set([
      PageMeta.Orders.service.name,
      PageMeta.Invoices.service.name,
      PageMeta.Quotes.service.name,
    ])

    const authStore = useAuthStore()
    const notify = useNotificationsStore()

    // ensure store has fetched guest permissions before we check anything
    await authStore.init('Router Guard')

    // The emailed set-password link was already moved into memory and stripped from the address.
    // Its calls are anonymous, so any local session state (stale, expired, a signed-in user whose
    // role lacks the anonymous row, a failed permission fetch) must not redirect and lose it.
    // Entering the page signs nobody out; success clears local auth state itself.
    if (to.name === PageMeta.Auth.setPassword.name) return true

    const ability = authStore.getAbility('Router Guard')
    const permissions = authStore.getPermissions('Router Guard')
    const authStatus = authStore.getAuthStatus()
    const toSubject = to.meta.subject
    const isServiceGuestRoute = serviceGuestRouteNames.has(to.name)

    const persistGuestLobbyContext = (matchedGuestPermission, reason) => {
      const returnRoute = captureAuthReturnRoute(to, { owner: null })

      if (!returnRoute) {
        log.warn(`${reason} -> service return route was unsafe to persist`)
        authStore.resetPageMeta()

        return false
      }

      authStore.setPageMeta(returnRoute, `Router Guard (${reason})`, true)
      authStore.setMatchedPermission(matchedGuestPermission, true)

      return true
    }

    const redirectToLogin = reason => {
      if (to.name === PageMeta.Auth.login.name) {
        return
      }

      if (authRouteNames.has(to.name)) {
        log.debug(`${reason} -> preserving the existing return route across an auth page`)

        return { name: PageMeta.Auth.login.name }
      }

      const returnRoute = captureAuthReturnRoute(to, { owner: authReturnRouteOwner(authStore) })

      if (returnRoute) {
        authStore.setPageMeta(returnRoute, `Router Guard (${reason})`, true)
      } else {
        log.warn(`${reason} -> intended return route was unsafe to persist`)
        authStore.resetPageMeta()
      }

      return { name: PageMeta.Auth.login.name }
    }

    const redirectGuestRouteToLobby = async reason => {
      log.warn(`${reason} -> resetting guest auth and redirecting to auth lobby`)

      authStore.resetGuestAuthForLobby()
      await nextTick()
      await authStore.init(`Router Guard (${reason} -> anon)`, true)

      const refreshedPermissions =
        authStore.getPermissions(`Router Guard (${reason} -> anon)`) ?? []

      const refreshedMatchedPermission =
        refreshedPermissions.find(permission => permission.subject === toSubject) ?? null

      if (!refreshedMatchedPermission) {
        log.error(`${reason} -> no matched guest permission found after anon re-init`)

        return redirectToLogin(`${reason}-missing-refreshed-permission`)
      }

      if (!persistGuestLobbyContext(refreshedMatchedPermission, reason)) {
        return { name: PageMeta.Auth.login.name }
      }

      return { name: PageMeta.Auth.lobby.name }
    }

    if (
      authStatus.isGuest &&
      isServiceGuestRoute &&
      (authStatus.isTokenExpired ||
        useAuthHelpers().getTokenExpiry(authStore.token) <= Date.now() / 1000)
    ) {
      return await redirectGuestRouteToLobby('expired-service-guest-token')
    }

    if (!ability) {
      log.debug('Ability is null (permissions fetch failed) -> redirecting to login')

      return redirectToLogin('ability-unavailable')
    }

    // Stale session detection: cookies expired but localStorage permissions remained
    // This is the root cause of the "ghost auth" state where CASL thinks user is authorized
    // Only applies when a previously authenticated user's session expired — fresh anon
    // permissions for guest/public pages must not be caught here.
    if (
      !authStatus.isAuthenticated &&
      !authStore.headers &&
      !authStore.token &&
      authStore.permissions?.length > 0 &&
      (authStore.userData || authStore.role)
    ) {
      if (isServiceGuestRoute) {
        return await redirectGuestRouteToLobby('stale-service-guest-session')
      }

      log.debug('Stale authenticated context -> resetting')
      authStore.resetState()
      await authStore.init('Router Guard (post-reset)', true)

      // The anonymous request page holds nothing a redirect could lose.
      if (to.name === PageMeta.Auth.forgotPassword.name) return true

      return redirectToLogin('stale-user-session')
    }

    const isRenewableExpiredSession =
      authStatus.isAuthenticated && authStatus.isTokenExpired && authStatus.isTokenRenewAllowed

    // The waiting page performs the renewal, so it must open without a tenant permission:
    // CASL admission would send it to not-authorized, whose expiry branch returns here forever.
    // This admits only the recovery page itself. Every other route keeps its own checks, and a
    // refresh lock held by another tab still lets the page mount and wait for that tab.
    if (to.name === PageMeta.Auth.waiting.name && isRenewableExpiredSession) {
      endMeasurement(guardMeasurement, {
        result: 'allow-navigation',
        reason: 'token-renewal-in-waiting-room',
      })

      return true
    }

    const authTokenExpired = authStatus.isTokenExpired && authStatus.isTokenRenewAllowed

    const matchedPermission = (permissions ?? []).find(
      p => p.subject === toSubject && (!to.meta.action || p.action === to.meta.action)
    )

    const conditions = matchedPermission?.conditions ?? null

    const isLoginRequired = conditions?.isLoginRequired ?? true
    const guestAuthenticationRequired = conditions?.guestAuthenticationRequired
    const apiGuidRequired = matchedPermission?.apiGuidRequired ?? false
    const authorizationHeaderRequired = matchedPermission?.authorizationHeaderRequired ?? false

    // Optimized token expiry handling - only redirect lock-holder tab to waiting room
    if (authStatus.isAuthenticated && authTokenExpired) {
      const lockActive = StorageManager().isLockActive(StorageKey.RefreshInProg)

      if (!lockActive) {
        log.debug('Token is expired -> Redirecting to Auth Waiting Room')
        endMeasurement(guardMeasurement, {
          result: 'redirect-to-waiting',
          reason: 'token-expired',
          canRefreshHere: true,
        })

        const returnRoute = captureAuthReturnRoute(to, { owner: authReturnRouteOwner(authStore) })

        if (returnRoute) {
          authStore.setPageMeta(returnRoute, 'Router Guard', true)
        } else {
          log.warn('Token is expired but the intended return route was unsafe to persist')
          authStore.resetPageMeta()
        }

        //authStore.setMatchedPermission(matchedPermission)

        return { name: PageMeta.Auth.waiting.name }
      } else {
        // Another tab is refreshing; let navigation continue, API layer will queue requests
        log.warn('Token expired but refresh in progress in another tab - allowing navigation')

        /*endMeasurement(guardMeasurement, {
          result: 'allow-navigation',
          reason: 'refresh-in-progress',
          canRefreshHere: false
        })*/
        //return true
      }
    }

    /*
    if (!authStatus.isAuthHeaderSet && authorizationHeaderRequired) {
      log('e', '[Router Guard] Authorization Header Required But Not Set -> resetting state')
      authStore.resetState()
      return { name: PageMeta.Auth.login.name }
    }

    if (!authStatus.isApiHeaderSet && apiGuidRequired) {
      log('e', '[Router Guard] API GUID Required But Not Set -> resetting state')
      authStore.resetState()
      return { name: PageMeta.Auth.login.name }
    }*/

    if (to.meta.redirectIfLoggedIn && authStatus.isAuthenticated && authStatus.isUser) {
      log.debug('User is logged in, selecting an admitted landing page')

      return admittedLandingRoute() ?? { name: PageMeta.Auth.notAuthorized.name }
    }

    if (
      ability.can(A.READ, to.meta.subject) && // if user can read the page
      !authStore.userData && // and they have no user data
      !authStatus.isAuthenticated && // and they arent authenticated
      !authStore.headers && // and they have no headers
      !to.meta.notLoggedIn && // and they arent not logged in
      matchedPermission && // but they have a matched permission
      isLoginRequired &&
      !guestAuthenticationRequired // let guest auth pages fall through to lobby redirect
    ) {
      log.warn('User Data, Headers, or Authenticated Status Not Set -> resetting state')

      return redirectToLogin('user-authentication-required')
    }

    if (!matchedPermission && !authStatus.isAuthenticated) {
      if (isServiceGuestRoute) {
        return await redirectGuestRouteToLobby('missing-service-guest-permission')
      }

      log.debug('No matched permission for unauthenticated route')

      return redirectToLogin('missing-permission')
    }

    if (!toSubject) {
      log.error('No Subject Set For This Page')
      log.error('Add permissions at @/@vxapp/Enums/PageMeta.js', to.fullPath)
      if (authStatus.isUser) {
        return { name: PageMeta.Auth.notAuthorized.name }
      } else {
        log.warn('No subject set, redirecting to login page')

        return redirectToLogin('missing-page-subject')
      }
    }

    if (conditions && guestAuthenticationRequired && !authStatus.isAuthenticated) {
      log.debug('Guest Auth Required -> Redirecting to Authentication Lobby')
      log.debug('Calling AuthStore To setPageMeta & setMatchedPermission')

      if (!persistGuestLobbyContext(matchedPermission, 'guest-auth-required')) {
        return { name: PageMeta.Auth.login.name }
      }

      return { name: PageMeta.Auth.lobby.name }
    }

    const isAbilityAllowed =
      (authStatus.isAuthenticated &&
        authStatus.isUser &&
        to.name === PageMeta.Auth.notAuthorized.name) ||
      canPageMeta(to.meta, to.name)

    if (!isAbilityAllowed && authStatus.isUser) {
      log.error('User Not Authorized To View Page', to.meta)

      notify.error({ error: Errors.Auth.PAGE_FORBIDDEN, pageSubject: toSubject })

      return { name: PageMeta.Auth.notAuthorized.name }

      //return false // prevent navigation
    }

    // Check for below cases unless the page doesnt require a login
    if (matchedPermission && isLoginRequired && !to.meta.notLoggedIn) {
      if (apiGuidRequired && !authStore.api) {
        log.error('API GUID Required But Not Set')
        authStore.resetState()

        return redirectToLogin('missing-api-guid')
      }

      if (authorizationHeaderRequired && !authStore.headers) {
        log.error('Authorization Header Required But Not Set')
        authStore.resetState()

        return redirectToLogin('missing-authorization-header')
      }
    }

    if (isAbilityAllowed) {
      endMeasurement(guardMeasurement, {
        result: 'allow-navigation',
        reason: 'ability-allowed',
        isAbilityAllowed: true,
      })

      return true
    }

    // Prevent redirect loops
    const ONE_MINUTE = 60000
    const { count } = authStore.abilityBlockHandler(ONE_MINUTE)
    if (count >= 5) {
      log.warn('abilityBlockHandler Count Exceeded -> Resetting State')
      authStore.resetState()

      endMeasurement(guardMeasurement, {
        result: 'redirect-to-login',
        reason: 'too-many-ability-blocks',
        count: count,
      })

      return redirectToLogin('too-many-ability-blocks')
    }

    log.error('blocked by ability', to.meta)
    log.error('blocked by ability (from)', from.meta)

    if (from.meta.subject === PageMeta.Auth.waiting.meta.subject) {
      log.error('redir to not auth', from.meta)

      return { name: PageMeta.Auth.notAuthorized.name }

      //return { name: PageMeta.Auth.waiting.name }
    }

    endMeasurement(guardMeasurement, {
      result: 'redirect-to-login',
      reason: 'ability-check-failed',
      isAbilityAllowed: false,
    })

    return redirectToLogin('ability-check-failed')
  })
}
